import { type Campaign, FUNNEL_STAGES, type FunnelStage } from '../api.ts';
import { FUNNEL_LABEL } from '../BriefDetailsFields.tsx';
import { CAMPAIGN_COLOR_VAR } from './lines.ts';
import { type BoardCard, type BoardColumn, groupByColumn } from './model.ts';

/**
 * Swimlanes (US-017): the board cut into rows by campaign, brand book or
 * funnel stage, each row across all seven columns. The lanes and their order
 * are Design reference § Swimlanes.
 */

export type BoardGroup = 'campaign' | 'book' | 'funnel';

/** The **Group** select; `''` is no grouping and stays out of the address. */
export const BOARD_GROUPS: readonly { readonly value: BoardGroup | ''; readonly label: string }[] = [
  { value: '', label: 'None' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'book', label: 'Brand book' },
  { value: 'funnel', label: 'Funnel stage' },
];

export function isGroup(value: string | null): value is BoardGroup {
  return value === 'campaign' || value === 'book' || value === 'funnel';
}

/** The lane of the cards that have no campaign, brand book or stage. */
export const NO_LANE = 'none';

export interface Lane {
  /** A campaign or brand book id, a funnel stage, or `none`; stable, so it can key storage. */
  readonly id: string;
  readonly name: string;
  /** A CSS colour for the lane's dot. */
  readonly color: string;
  /** Set on a campaign's own lane. */
  readonly campaign: Campaign | null;
  readonly columns: Record<BoardColumn, BoardCard[]>;
  readonly count: number;
}

const NEUTRAL_DOT = CAMPAIGN_COLOR_VAR.gray;

/** Funnel stages carry no colour of their own; these run cold to hot. */
const FUNNEL_COLOR: Record<FunnelStage, string> = {
  cold: CAMPAIGN_COLOR_VAR.blue,
  warm: CAMPAIGN_COLOR_VAR.amber,
  hot: CAMPAIGN_COLOR_VAR.red,
  retention: CAMPAIGN_COLOR_VAR.green,
};

const NO_LANE_NAME: Record<BoardGroup, string> = {
  campaign: 'No campaign',
  book: 'No brand book',
  funnel: 'No stage',
};

/** The lane a card belongs to; a card with several stages goes under its first. */
export function laneOf(card: BoardCard, group: BoardGroup): string {
  const item = card.type === 'idea' ? card.idea : card.content;
  if (group === 'campaign') return item.campaignId ?? NO_LANE;
  if (group === 'book') {
    return (card.type === 'idea' ? card.batch.brandBookId : card.content.brandBook?.id) ?? NO_LANE;
  }
  const stages = item.briefDetails.funnelStages;
  return FUNNEL_STAGES.find((stage) => stages.includes(stage)) ?? NO_LANE;
}

/** Non-archived campaigns by start day (none last), then name. */
function compareCampaigns(a: Campaign, b: Campaign): number {
  if (a.startsOn !== b.startsOn) {
    if (a.startsOn === null) return 1;
    if (b.startsOn === null) return -1;
    return a.startsOn < b.startsOn ? -1 : 1;
  }
  return a.name.localeCompare(b.name);
}

/**
 * The lanes of `cards`, in the order § Swimlanes gives. Every live campaign
 * and every brand book has a lane even when empty (a place to see it is
 * quiet); an archived campaign only while it still has cards. The "No …"
 * lane comes last, and only when something stands in it.
 */
export function buildLanes(
  cards: readonly BoardCard[],
  group: BoardGroup,
  campaigns: readonly Campaign[],
  brandBooks: readonly { readonly id: string; readonly name: string }[],
): Lane[] {
  const byLane = new Map<string, BoardCard[]>();
  for (const card of cards) {
    const id = laneOf(card, group);
    const list = byLane.get(id);
    if (list === undefined) byLane.set(id, [card]);
    else list.push(card);
  }

  const heads: { id: string; name: string; color: string; campaign: Campaign | null }[] = [];
  if (group === 'campaign') {
    const live = campaigns.filter((campaign) => campaign.archivedAt === null).sort(compareCampaigns);
    const archived = campaigns
      .filter((campaign) => campaign.archivedAt !== null && byLane.has(campaign.id))
      .sort(compareCampaigns);
    for (const campaign of [...live, ...archived]) {
      heads.push({ id: campaign.id, name: campaign.name, color: CAMPAIGN_COLOR_VAR[campaign.color], campaign });
    }
  } else if (group === 'book') {
    for (const book of [...brandBooks].sort((a, b) => a.name.localeCompare(b.name))) {
      heads.push({ id: book.id, name: book.name, color: NEUTRAL_DOT, campaign: null });
    }
  } else {
    for (const stage of FUNNEL_STAGES) {
      heads.push({ id: stage, name: FUNNEL_LABEL[stage], color: FUNNEL_COLOR[stage], campaign: null });
    }
  }
  // A card whose campaign or book is not in the lists (a read in between)
  // still has to show somewhere: the "No …" lane takes it.
  const known = new Set(heads.map((head) => head.id));
  const orphans = [...byLane].flatMap(([id, list]) => (known.has(id) ? [] : list));
  if (orphans.length > 0) {
    heads.push({ id: NO_LANE, name: NO_LANE_NAME[group], color: NEUTRAL_DOT, campaign: null });
  }

  return heads.map((head) => {
    const list = head.id === NO_LANE ? orphans : (byLane.get(head.id) ?? []);
    return { ...head, columns: groupByColumn(list), count: list.length };
  });
}
